import { SkinnedOwnerStageElement } from "./skinned-owner-stage.js";
import { escapeHtml } from "../util/escape-html.js";
const QUERY = "home.summary.v1";
const FEED_QUERY = "feeds.timeline.v1";
export const HOME_STAGE_TAG = "node-home-stage";
export const HOME_QUERY = QUERY;
export const HOME_FEED_QUERY = FEED_QUERY;
/** Feed rows rendered below the node-status post; capped so a large backlog can't unbound the DOM. */
const FEED_RENDER_LIMIT = 12;
/** Skeleton widths used only when the feed is genuinely empty/unavailable. */
const SKELETON_SHELLS = [[94, 62], [78, 88], [88, 46]];
/**
 * Owner stage: home — re-clothed to the original skin's dash-FEED layout (epic #1507
 * fidelity, stage 4/5). Ported from `SKIN/stages/default/home/view/dashboard-view.js`'s
 * `.dash-wrap` > `.dash-hd`/`.greet` header followed by `.dash-grid`, plus `widgets/
 * dashboard-cards.js`'s `.post` markup — verified real via explicit-file grep against the
 * vendored `client/ui/src/skin/css/legacy-numbered.css` (`.dash-wrap`, `.dash-hd .greet`,
 * `.dash-grid`, `.post`/`.post-hd`/`.pava`/`.pname`/`.post-body`/`.post-actions`/`.paction`
 * all carry real declarations there).
 *
 * DELIBERATE SCOPE CUT — the original's default view is a 3D WebGL "room"
 * (`<div class="d360" id="d360">…</div>`, a `<canvas>` orbited by a room-picker HUD) rendered
 * ABOVE `.dash-wrap`, with the feed reachable by scrolling/clicking a "show your feed" cue.
 * That room is NOT ported here — epic #1507 decision: it pulls in ~600KB of three.js, which
 * this stage's shared #1528 gzip bootstrap budget (`system/pwa/scripts/verify-build.mjs`,
 * held at 268KB) cannot absorb, and the room is decorative chrome around the feed, not the
 * content layer itself. The feed (`.dash-wrap`) is what's real/live-bindable, so it ships
 * alone, standing in as this stage's whole top-level view instead of sitting below a room
 * this budget can't afford. If the room ever becomes in-scope, it belongs in its own
 * lazy-loaded chunk (see `waves-canvas.ts`'s doc comment for the precedent this stage would
 * follow), not inline here.
 *
 * Real binding kept: `home.summary.v1`'s `displayName`/`homeNodeId`/`status`/`lastSyncedAt`
 * drive both the `.greet` header ("Your news, <em>{displayName}.</em>") and the feed's FIRST
 * `.post` — the real node-status row, trimmed into the original's own post shape (`.pava`
 * initials + `.pname` homeNodeId + `.ptime` last-synced, `.post-body` status sentence) instead
 * of the ad hoc `.ui-card`/`.act-row` a prior pass shipped. `.dash-grid`'s second column (the
 * original's weather/markets/calendar/to-do widget rail) is genuinely dead in the source itself
 * — `.dash-grid>div:nth-child(2){display:none}` in the vendored CSS — so it's not rendered at
 * all here (dropping the prior pass's "Coming soon" widget markup this task found dead-on-
 * arrival, not merely undecorated).
 *
 * The rest of the feed (every `.post` after the status row) is now REAL (#1539): it binds
 * `feeds.timeline.v1` — the kernel live query registered in `system/pwa/src/kernel-runtime.ts`
 * off `OwnerViewService.listFeed` → `HomeNodeOwnerViewRepository.listFeed` → the home node's
 * `core.notifications.list` capability. That query was already registered but had NO UI
 * consumer anywhere in `client/ui`, which is exactly why this feed rendered as permanent
 * skeletons; wiring the existing query in is the whole change, no new sync path was added.
 *
 * Like `wallet-stage`'s balance readout, the feed is a SECOND, self-contained subscription
 * (`#startFeed`/`#applyFeed`) rather than this stage's primary `query`, because
 * `SkinnedOwnerStageElement` drives exactly one query through its lifecycle and the greet
 * header / status post must keep coming from `home.summary.v1`. It only ever touches its own
 * `[data-home-feed]` subtree and is re-applied after every `renderView` re-render.
 *
 * Honest-empty is preserved: when the feed query returns no items — or fails/is unavailable
 * (offline, capability undeclared, home node unreachable) — the same styled, empty `.post`
 * skeletons this stage shipped before render instead. The original's fabricated named posters
 * (Jess Park / Sarah Kim / etc.) and its invented `.post--contest` banner remain unported. No
 * author, handle, timestamp, or body text is invented anywhere in this file; every rendered
 * value comes from a `FeedItem` field (`title`/`body`/`createdAt`/`read`).
 */
export class HomeStageElement extends SkinnedOwnerStageElement {
    query = QUERY;
    emptyMessage = "No home data available offline";
    #feed;
    #feedClient;
    #feedGeneration = 0;
    #feedUnsubscribe;
    configure(options) {
        super.configure(options);
        this.#feedClient = options.client;
        if (this.isConnected)
            this.#startFeed();
    }
    connectedCallback() {
        super.connectedCallback();
        this.#startFeed();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#feedGeneration += 1;
        this.#feedUnsubscribe?.();
        this.#feedUnsubscribe = undefined;
    }
    renderView(root, rows) {
        const summary = rows[0] ?? {};
        const displayName = stringField(summary.displayName);
        const homeNodeId = stringField(summary.homeNodeId) ?? "unknown node";
        const status = stringField(summary.status) ?? "unknown";
        const lastSyncedAt = stringField(summary.lastSyncedAt);
        root.innerHTML = `
      <div class="dash-wrap" data-home-dashboard>
        <div class="dash-hd">
          <h1 class="greet">Your news, <em>${escapeHtml(displayName ?? "here")}.</em></h1>
        </div>
        <div class="dash-grid">
          <div>
            ${renderStatusPost(displayName, homeNodeId, status, lastSyncedAt)}
            <div class="home-feed" data-home-feed style="display:contents;">${renderSkeletonRun()}</div>
          </div>
        </div>
      </div>
    `;
        this.#applyFeed();
    }
    #startFeed() {
        this.#feedUnsubscribe?.();
        this.#feedUnsubscribe = undefined;
        const client = this.#feedClient;
        if (client === undefined)
            return;
        const generation = ++this.#feedGeneration;
        this.#feedUnsubscribe = client.liveQuery(FEED_QUERY, {}, () => {
            if (generation !== this.#feedGeneration)
                return;
            // The feed is an ordered timeline, so a diff can't be folded into a stable local order
            // the way `SkinnedOwnerStageElement` folds keyed rows. Re-read the authoritative
            // ordering from the same query the diff invalidated.
            void this.#loadFeed(generation, client);
        });
        void this.#loadFeed(generation, client);
    }
    async #loadFeed(generation, client) {
        try {
            const rows = await client.query(FEED_QUERY, {});
            if (generation !== this.#feedGeneration)
                return;
            this.#feed = rows;
            this.#applyFeed();
        }
        catch {
            // Offline / undeclared capability / unreachable home node: leave the skeleton run in
            // place rather than claiming an empty feed.
            if (generation !== this.#feedGeneration)
                return;
            this.#feed = undefined;
            this.#applyFeed();
        }
    }
    #applyFeed() {
        const container = this.querySelector("[data-home-feed]");
        if (container === null)
            return;
        const items = (this.#feed ?? []).filter(isRenderableFeedItem).slice(0, FEED_RENDER_LIMIT);
        container.innerHTML = items.length === 0
            ? renderSkeletonRun()
            : items.map((item) => renderFeedPost(item)).join("");
    }
}
/** A feed row is renderable only when the fields the post markup actually shows are present. */
function isRenderableFeedItem(row) {
    return stringField(row.title) !== undefined || stringField(row.body) !== undefined;
}
function renderSkeletonRun() {
    return SKELETON_SHELLS.map(([nameWidth, bodyWidth]) => renderSkeletonPost(nameWidth, bodyWidth)).join("");
}
/**
 * A real `feeds.timeline.v1` row in the original's `.post` shape. Every visible value is a
 * `FeedItem` field: `.pava` initials derive from `title`, `.pname` is `title`, `.ptime` is
 * `createdAt`, `.post-body` is `body`. Unread rows keep the status post's gold `.pava`
 * gradient; read rows get the muted `var(--bg-1)` fill the skeletons use — both are existing
 * skin tokens, no hardcoded hex. `.paction` buttons stay `disabled`: no like/comment/share
 * capability is wired.
 */
function renderFeedPost(item) {
    const title = stringField(item.title);
    const body = stringField(item.body);
    const createdAt = stringField(item.createdAt);
    const read = item.read === true;
    const avatarStyle = read
        ? "background:var(--bg-1);box-shadow:inset 0 0 0 1px var(--hr);"
        : "background:linear-gradient(135deg,var(--tan),var(--gold-dp));";
    return `
            <article class="post" data-home-feed-item data-home-feed-read="${read ? "true" : "false"}">
              <div class="post-hd">
                <div class="pava" style="${avatarStyle}">${escapeHtml(read ? "" : initialsFor(title, title ?? ""))}</div>
                <div>
                  <div class="pname">${escapeHtml(title ?? "")}</div>
                </div>
                <span class="ptime">${escapeHtml(formatFeedTime(createdAt))}</span>
              </div>
              <div class="post-body">${escapeHtml(body ?? "")}</div>
              <div class="post-actions">
                <button class="paction" type="button" disabled>${LIKE_GLYPH}</button>
                <button class="paction" type="button" disabled>${COMMENT_GLYPH}</button>
                <button class="paction" type="button" disabled>${SHARE_GLYPH} Share</button>
              </div>
            </article>`;
}
/** Relative time off a real `createdAt`; never a fabricated stamp when the field is absent. */
function formatFeedTime(value) {
    if (value === undefined)
        return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime()))
        return "";
    const diffMinutes = Math.round((Date.now() - parsed.getTime()) / 60_000);
    if (diffMinutes < 1)
        return "Just now";
    if (diffMinutes < 60)
        return `${diffMinutes}m`;
    const diffHours = Math.round(diffMinutes / 60);
    if (diffHours < 24)
        return `${diffHours}h`;
    return parsed.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
function renderStatusPost(displayName, homeNodeId, status, lastSyncedAt) {
    return `
            <article class="post" data-home-status>
              <div class="post-hd">
                <div class="pava" style="background:linear-gradient(135deg,var(--tan),var(--gold-dp));">${escapeHtml(initialsFor(displayName, homeNodeId))}</div>
                <div>
                  <div class="pname">${escapeHtml(homeNodeId)}</div>
                </div>
                <span class="ptime">${escapeHtml(formatLastSynced(lastSyncedAt))}</span>
              </div>
              <div class="post-body">${escapeHtml(statusSentence(status))}</div>
            </article>`;
}
/**
 * Empty skeleton `.post` shell for a feed row this stage has no live data for yet (#1539) —
 * muted `.pava`/`.pname`/`.post-body` bars (`var(--bg-1)`, the same "empty avatar" fill the
 * agents stage's `.cg-run-ava`/`.cg-sched-ava` placeholders already use — `skin/css/fixes.css`)
 * plus `disabled` `.paction` buttons. `nameWidth`/`bodyWidth` are percentages so the 3 shells
 * don't render as identical, obviously-templated bars.
 */
function renderSkeletonPost(nameWidth, bodyWidth) {
    const barStyle = "background:var(--bg-1);box-shadow:inset 0 0 0 1px var(--hr);";
    return `
            <article class="post post--skeleton" data-home-skeleton aria-hidden="true">
              <div class="post-hd">
                <div class="pava" style="${barStyle}"></div>
                <div>
                  <div class="pname" style="width:${nameWidth}px;height:11px;border-radius:6px;${barStyle}"></div>
                </div>
                <span class="ptime" style="display:inline-block;width:46px;height:9px;border-radius:5px;${barStyle}"></span>
              </div>
              <div class="post-body">
                <span style="display:block;width:${bodyWidth}%;height:10px;border-radius:5px;margin-bottom:6px;${barStyle}"></span>
                <span style="display:block;width:${Math.max(30, bodyWidth - 28)}%;height:10px;border-radius:5px;${barStyle}"></span>
              </div>
              <div class="post-actions">
                <button class="paction" type="button" disabled>${LIKE_GLYPH}</button>
                <button class="paction" type="button" disabled>${COMMENT_GLYPH}</button>
                <button class="paction" type="button" disabled>${SHARE_GLYPH} Share</button>
              </div>
            </article>`;
}
const LIKE_GLYPH = '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M7 12 C 3 9 1 7 1 4.5 C 1 2.5 2.5 1 4.5 1 C 5.8 1 6.5 1.7 7 2.5 C 7.5 1.7 8.2 1 9.5 1 C 11.5 1 13 2.5 13 4.5 C 13 7 11 9 7 12 Z"/></svg>';
const COMMENT_GLYPH = '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M12 7 C 12 9.5 9.8 11.5 7 11.5 C 6.2 11.5 5.5 11.4 4.9 11.1 L 2 12 L 2.9 9.5 C 2.3 8.8 2 7.9 2 7 C 2 4.5 4.2 2.5 7 2.5 C 9.8 2.5 12 4.5 12 7 Z"/></svg>';
const SHARE_GLYPH = '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M7 1.5 L7 9 M4 4.5 L7 1.5 L10 4.5"/><path d="M2.5 8 V12 Q2.5 12.5 3 12.5 L11 12.5 Q11.5 12.5 11.5 12 V8"/></svg>';
export function registerHomeStageElement() {
    if (!customElements.get(HOME_STAGE_TAG)) {
        customElements.define(HOME_STAGE_TAG, HomeStageElement);
    }
}
function stringField(value) {
    return typeof value === "string" && value.length > 0 ? value : undefined;
}
/** Deterministic 2-letter initials off real fields (`displayName`, falling back to
 * `homeNodeId`) — never a fabricated name. */
function initialsFor(displayName, homeNodeId) {
    const source = displayName ?? homeNodeId;
    const words = source.trim().split(/[\s._-]+/u).filter((word) => word.length > 0);
    const first = words[0];
    if (first === undefined)
        return "ND";
    const second = words[1];
    if (second === undefined)
        return first.slice(0, 2).toUpperCase();
    return `${first.charAt(0)}${second.charAt(0)}`.toUpperCase();
}
function statusSentence(status) {
    switch (status) {
        case "online": return "This node is online and reachable.";
        case "offline": return "This node is offline right now.";
        default: return "This node's status is unknown.";
    }
}
function formatLastSynced(value) {
    if (value === undefined)
        return "Never synced";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime()))
        return "Never synced";
    return `Synced ${parsed.toLocaleString()}`;
}
//# sourceMappingURL=home-stage.js.map